import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAppAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { usePanel } from '../context/PanelContext';
import MINECRAFT_BLOCKS from '../data/minecraftBlocks';
import { spriteMap, SPRITE_SIZE } from '../data/itemSpriteMap';
import useTurnstileToken from '../utils/useTurnstileToken';
import './ControlPanelPage.css';

const PARTYKIT_HOST = process.env.REACT_APP_PARTYKIT_HOST || 'thetopia-live.lafless.partykit.dev';

/* ── Block icon for control panel ── */
const CP_BLOCK_FALLBACKS = {
  water: 'water_bucket',
  lava: 'lava_bucket',
  powder_snow: 'powder_snow_bucket',
  chain: 'iron_chain',
  skeleton_head: 'skeleton_skull',
};

function CpBlockIcon({ block, size = 20 }) {
  const spriteKey = spriteMap[block] ? block : CP_BLOCK_FALLBACKS[block];
  const pos = spriteKey && spriteMap[spriteKey];
  if (pos) {
    const scale = size / SPRITE_SIZE;
    return (
      <div
        style={{
          width: size,
          height: size,
          backgroundImage: 'url(/img/item-sprites.png)',
          backgroundPosition: `-${pos[0] * scale}px -${pos[1] * scale}px`,
          backgroundSize: `${1248 * scale}px ${1248 * scale}px`,
          imageRendering: 'pixelated',
          flexShrink: 0,
          display: 'inline-block',
          verticalAlign: 'middle',
        }}
      />
    );
  }
  // Fallback to individual PNG
  return (
    <div
      style={{
        width: size,
        height: size,
        backgroundImage: `url(/img/items/${block}.png)`,
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        imageRendering: 'pixelated',
        flexShrink: 0,
        display: 'inline-block',
        verticalAlign: 'middle',
      }}
    />
  );
}

export default function ControlPanelPage() {
  const { slug } = useParams();
  const { getToken } = useAppAuth();
  // Control panel uses viewer token directly — admin actions go through Vercel API with Clerk auth
  const sessionToken = process.env.REACT_APP_PARTYKIT_VIEWER_TOKEN || '';
  const { notify } = useNotification();
  const { events, loading: eventsLoading } = usePanel();

  const event = useMemo(() => events?.find(e => e.slug === slug), [events, slug]);
  const webAddon = event?.webAddon || 'command-voting';
  console.log('[CP-DEBUG] slug:', slug, 'eventsLoading:', eventsLoading, 'eventsCount:', events?.length, 'event found:', !!event, 'webAddon:', webAddon, 'token:', sessionToken?.slice(0, 10) + '...');
  const isChunkDelete = webAddon === 'chunk-delete';
  const isBlockDelete = webAddon === 'block-delete';
  const isWyr = webAddon === 'would-you-rather';

  const [roomState, setRoomState] = useState(null);
  const [connState, setConnState] = useState('verifying'); // verifying | disconnected | connecting | connected
  const [busy, setBusy] = useState(false);

  const [editDuration, setEditDuration] = useState(300);
  const [editIntermission, setEditIntermission] = useState(30);
  const [editBorderRadius, setEditBorderRadius] = useState(500);
  const [keywordInput, setKeywordInput] = useState('');
  const [blockSearchInput, setBlockSearchInput] = useState('');
  const [editIntensified, setEditIntensified] = useState(false);

  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const initialSynced = useRef(false);

  // Reset all state when switching between control panels
  useEffect(() => {
    setRoomState(null);
    setBusy(false);
    setEditDuration(300);
    setEditIntermission(30);
    setEditBorderRadius(500);
    setKeywordInput('');
    setEditIntensified(false);
    initialSynced.current = false;
  }, [slug, webAddon]);

  // WebSocket — route to correct party based on addon type
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    if (!slug || eventsLoading) return;
    if (!sessionToken) {
      setConnState('verifying');
      return;
    }

    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const partyPath = isChunkDelete ? `/parties/chunk/${slug}` : isBlockDelete ? `/parties/block/${slug}` : isWyr ? `/parties/wyr/${slug}` : `/party/${slug}`;
    const wsUrl = `${protocol}://${PARTYKIT_HOST}${partyPath}`;
    console.log('[CP-DEBUG] WebSocket setup:', { partyPath, wsUrl, isWyr, isBlockDelete, isChunkDelete });

    const doConnect = () => {
      if (!mountedRef.current) return;
      console.log('[CP-DEBUG] doConnect() called');
      setConnState('connecting');

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) { ws.close(); return; }
        console.log('[CP-DEBUG] WebSocket OPEN, sending join with token:', sessionToken?.slice(0, 10) + '...');
        setConnState('connected');
        ws.send(JSON.stringify({ type: 'join', displayName: 'Admin', fingerprint: 'admin-panel', token: sessionToken }));
      };

      ws.onmessage = (evt) => {
        if (!mountedRef.current) return;
        try {
          const msg = JSON.parse(evt.data);
          console.log('[CP-DEBUG] MSG:', msg.type, msg.type === 'error' ? msg.message : '', msg.type === 'state' ? `round:${msg.roundState}` : '');
          if (msg.type === 'state') {
            setRoomState(msg);
            // Always sync intensified from server (other admins may toggle it)
            if (msg.intensified != null) setEditIntensified(msg.intensified);
            if (!initialSynced.current) {
              setEditDuration(msg.roundDurationSec || 300);
              setEditIntermission(msg.intermissionSec || 30);
              if (msg.borderRadius) setEditBorderRadius(msg.borderRadius);
              initialSynced.current = true;
            }
          } else if (msg.type === 'update') {
            setRoomState(prev => {
              if (!prev) return null;
              const { type, ...fields } = msg;
              return { ...prev, ...fields };
            });
          } else if (msg.type === 'players') {
            setRoomState(prev => prev ? { ...prev, players: msg.players } : null);
          }
        } catch {}
      };

      ws.onclose = (e) => {
        console.log('[CP-DEBUG] WebSocket CLOSED code:', e.code, 'reason:', e.reason);
        wsRef.current = null;
        if (mountedRef.current) {
          setConnState('disconnected');
          reconnectRef.current = setTimeout(doConnect, 2000);
        }
      };

      ws.onerror = (e) => {
        console.error('[CP-DEBUG] WebSocket ERROR:', e);
        if (mountedRef.current) setConnState('disconnected');
      };
    };

    doConnect();

    return () => {
      mountedRef.current = false;
      clearTimeout(reconnectRef.current);
      if (wsRef.current) {
        wsRef.current.onclose = null; // prevent reconnect on intentional close
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [slug, isChunkDelete, isBlockDelete, isWyr, eventsLoading, sessionToken]);

  // Admin action
  const ACTION_LABELS = {
    'start': 'Event started',
    'pause': 'Event paused',
    'resume': 'Event resumed',
    'reset': 'Event reset',
    'end-round': 'Round ended',
    'set-duration': 'Round duration updated',
    'set-intermission': 'Intermission duration updated',
    'delete-command': 'Command deleted',
    'add-blocked-keyword': 'Keyword blocked',
    'remove-blocked-keyword': 'Keyword unblocked',
    'add-allowed-command': 'Command allowed',
    'remove-allowed-command': 'Command removed from allowlist',
    'add-blocked-block': 'Block blacklisted',
    'remove-blocked-block': 'Block unblacklisted',
    'restore-deleted-block': 'Deleted block restored',
    'set-intensified': 'Intensified mode toggled',
    'clear-deleted-blocks': 'All deleted blocks cleared',
  };

  const sendAction = async (action, extra = {}) => {
    setBusy(true);
    try {
      const token = await getToken();
      const res = await fetch('/api/admin/events?action=event-control', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ slug, action, webAddon, ...extra }),
      });
      if (!res.ok) {
        const text = await res.text();
        try { throw new Error(JSON.parse(text).error || 'Failed'); } catch { throw new Error(`Request failed (${res.status})`); }
      }
      const data = await res.json();
      notify(ACTION_LABELS[action] || action, 'success');
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleStart = () => {
    sendAction('start', {
      eventName: roomState?.eventName || slug,
      roundDurationSec: editDuration,
      intermissionSec: editIntermission,
      ...(isChunkDelete ? { borderRadius: editBorderRadius } : {}),
    });
  };

  const handleAddKeyword = () => {
    const kw = keywordInput.trim();
    if (kw.length < 2) return;
    sendAction('add-blocked-keyword', { keyword: kw });
    setKeywordInput('');
  };

  const toggleAllowedCmd = (cmd) => {
    const isAllowed = (roomState?.allowedCommands || []).some(
      ac => ac.toLowerCase() === cmd.toLowerCase()
    );
    if (isAllowed) {
      sendAction('remove-allowed-command', { command: cmd });
    } else {
      sendAction('add-allowed-command', { command: cmd });
    }
  };

  // Timer
  const [timeLeft, setTimeLeft] = useState(null);
  useEffect(() => {
    // Active round timer
    if (roomState?.roundState === 'active' && roomState?.roundStartedAt) {
      const tick = () => {
        const elapsed = Date.now() - roomState.roundStartedAt - (roomState.pausedElapsed || 0);
        const remaining = Math.max(0, (roomState.roundDurationSec * 1000) - elapsed);
        setTimeLeft(Math.ceil(remaining / 1000));
      };
      tick();
      const interval = setInterval(tick, 250);
      return () => clearInterval(interval);
    }

    // Intermission countdown
    if (roomState?.roundState === 'intermission' && roomState?.intermissionStartedAt) {
      const tick = () => {
        const elapsed = Date.now() - roomState.intermissionStartedAt - (roomState.pausedElapsed || 0);
        const remaining = Math.max(0, (roomState.intermissionSec * 1000) - elapsed);
        setTimeLeft(Math.ceil(remaining / 1000));
      };
      tick();
      const interval = setInterval(tick, 250);
      return () => clearInterval(interval);
    }

    // Paused - frozen timer
    if (roomState?.roundState === 'paused' && roomState?.pausedAt) {
      if (roomState.prePauseState === 'intermission' && roomState.intermissionStartedAt) {
        const elapsed = roomState.pausedAt - roomState.intermissionStartedAt - (roomState.pausedElapsed || 0);
        const remaining = Math.max(0, (roomState.intermissionSec * 1000) - elapsed);
        setTimeLeft(Math.ceil(remaining / 1000));
      } else if (roomState.roundStartedAt) {
        const elapsed = roomState.pausedAt - roomState.roundStartedAt - (roomState.pausedElapsed || 0);
        const remaining = Math.max(0, (roomState.roundDurationSec * 1000) - elapsed);
        setTimeLeft(Math.ceil(remaining / 1000));
      } else {
        setTimeLeft(null);
      }
      return;
    }

    setTimeLeft(null);
  }, [roomState?.roundStartedAt, roomState?.roundDurationSec, roomState?.pausedElapsed, roomState?.roundState, roomState?.pausedAt, roomState?.intermissionStartedAt, roomState?.intermissionSec, roomState?.prePauseState]);

  const formatTime = (s) => {
    if (s == null) return '--:--';
    return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
  };

  const rs = roomState?.roundState || 'idle';
  const isPaused = rs === 'paused';
  const isIdle = rs === 'idle';

  return (
    <section className="cp-page">
      <div className="cp-container">
        <div className="cp-top-bar">
          <div className="cp-header">
            <h1 className="cp-title">Control Panel</h1>
            <p className="cp-slug">/{slug}</p>
          </div>
          <div className={`cp-conn-badge cp-conn-${connState}`}>
            {connState === 'connected' ? 'Connected' : connState === 'connecting' ? 'Connecting' : connState === 'verifying' ? 'Verifying' : 'Disconnected'}
          </div>
        </div>

        {/* ── Top Row: Status+Moderation left, Actions+Config right ── */}
        <div className="cp-grid-top">
          {/* Left column */}
          <div className="cp-col">
            {/* Status */}
            <div className="cp-card">
              <div className="cp-card-label">Status</div>
              <div className="cp-status-row">
                <span className={`cp-state-badge cp-state-${rs}`}>
                  {rs.toUpperCase()}
                </span>
                <span className="cp-timer">{formatTime(timeLeft)}</span>
              </div>
              <div className="cp-status-meta">
                {roomState?.roundNumber > 0 && (
                  <span className="cp-round-num">Round {roomState.roundNumber}</span>
                )}
                <span className="cp-viewers">
                  <span className="cp-viewers-dot" />
                  {roomState?.connections ?? 0} watching
                </span>
              </div>
            </div>

            {/* WYR current question */}
            {isWyr && roomState?.question && (
              <div className="cp-card">
                <div className="cp-card-label">Current Question</div>
                <div style={{ padding: '12px 16px', display: 'flex', gap: '12px' }}>
                  <div style={{ flex: 1, padding: '10px', borderRadius: '8px', background: 'rgba(192,57,43,0.1)', border: '1px solid rgba(192,57,43,0.3)' }}>
                    <div style={{ color: '#eceef1', fontSize: '0.85rem', fontFamily: "'Minecraft', monospace", WebkitFontSmoothing: 'none', marginBottom: '4px' }}>{roomState.question.leftDesc}</div>
                    <div style={{ color: '#8e929c', fontSize: '0.75rem' }}>{roomState.leftVotes ?? 0} votes ({roomState.leftPct ?? 0}%)</div>
                  </div>
                  <div style={{ flex: 1, padding: '10px', borderRadius: '8px', background: 'rgba(41,128,185,0.1)', border: '1px solid rgba(41,128,185,0.3)' }}>
                    <div style={{ color: '#eceef1', fontSize: '0.85rem', fontFamily: "'Minecraft', monospace", WebkitFontSmoothing: 'none', marginBottom: '4px' }}>{roomState.question.rightDesc}</div>
                    <div style={{ color: '#8e929c', fontSize: '0.75rem' }}>{roomState.rightVotes ?? 0} votes ({roomState.rightPct ?? 0}%)</div>
                  </div>
                </div>
              </div>
            )}

            {/* Moderation (command-voting only) */}
            {!isChunkDelete && !isBlockDelete && !isWyr && <div className="cp-card">
              <div className="cp-card-label">Moderation</div>

              {/* Blocked keywords */}
              <div className="cp-mod-section">
                <label className="cp-config-label">Blocked Keywords</label>
                <div className="cp-mod-input-row">
                  <input
                    type="text"
                    className="cp-mod-input"
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddKeyword()}
                    placeholder="Add keyword..."
                    maxLength={50}
                  />
                  <button className="cp-btn cp-btn-xs" onClick={handleAddKeyword} disabled={busy}>Add</button>
                </div>
                {roomState?.blockedKeywords?.length > 0 && (
                  <div className="cp-tag-list">
                    {roomState.blockedKeywords.map((kw) => (
                      <span key={kw} className="cp-tag cp-tag-keyword">
                        {kw}
                        <button
                          className="cp-tag-x"
                          onClick={() => sendAction('remove-blocked-keyword', { keyword: kw })}
                        >&times;</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Allowed commands toggle grid */}
              <div className="cp-mod-section">
                <label className="cp-config-label">
                  Allowed Commands
                  <span className="cp-mod-count">
                    {roomState?.allowedCommands?.length || 0}/{roomState?.availableCommands?.length || 0}
                  </span>
                </label>
                {roomState?.availableCommands?.length > 0 && (
                  <div className="cp-cmd-grid">
                    {roomState.availableCommands.map((cmd) => {
                      const isOn = (roomState.allowedCommands || []).some(
                        ac => ac.toLowerCase() === cmd.toLowerCase()
                      );
                      return (
                        <button
                          key={cmd}
                          className={`cp-cmd-toggle ${isOn ? 'cp-cmd-toggle-on' : ''}`}
                          onClick={() => toggleAllowedCmd(cmd)}
                          disabled={busy}
                        >
                          {cmd}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>}

            {/* Block Blacklist (block-delete only) */}
            {isBlockDelete && <div className="cp-card">
              <div className="cp-card-label">Block Blacklist</div>
              <div className="cp-mod-section">
                <div className="cp-mod-input-row">
                  <input
                    type="text"
                    className="cp-mod-input"
                    value={blockSearchInput}
                    onChange={(e) => setBlockSearchInput(e.target.value)}
                    placeholder="Search blocks to blacklist..."
                    maxLength={100}
                  />
                </div>
                {blockSearchInput.length >= 2 && (() => {
                  const term = blockSearchInput.toLowerCase().replace(/\s+/g, '_');
                  const matches = MINECRAFT_BLOCKS
                    .filter(b => b.value.includes(term) || b.label.toLowerCase().includes(blockSearchInput.toLowerCase()))
                    .filter(b => !(roomState?.blockedBlocks || []).includes(b.value))
                    .slice(0, 12);
                  return matches.length > 0 ? (
                    <div className="cp-cmd-grid" style={{ marginTop: 6 }}>
                      {matches.map(b => (
                        <button
                          key={b.value}
                          className="cp-cmd-toggle"
                          onClick={() => { sendAction('add-blocked-block', { blockId: b.value }); setBlockSearchInput(''); }}
                          disabled={busy}
                          style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                        >
                          <CpBlockIcon block={b.value} size={16} />
                          {b.label}
                        </button>
                      ))}
                    </div>
                  ) : null;
                })()}
              </div>
              {roomState?.blockedBlocks?.length > 0 && (
                <div className="cp-tag-list" style={{ marginTop: 8 }}>
                  {roomState.blockedBlocks.map((id) => {
                    const block = MINECRAFT_BLOCKS.find(b => b.value === id);
                    return (
                      <span key={id} className="cp-tag cp-tag-keyword" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <CpBlockIcon block={id} size={14} />
                        {block?.label || id.replace(/_/g, ' ')}
                        <button
                          className="cp-tag-x"
                          onClick={() => sendAction('remove-blocked-block', { blockId: id })}
                        >&times;</button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>}

            {/* Deleted Blocks (block-delete only) */}
            {isBlockDelete && roomState?.deletedBlocks?.length > 0 && <div className="cp-card">
              <div className="cp-card-label">Deleted Blocks</div>
              <div className="cp-tag-list" style={{ marginTop: 4 }}>
                {roomState.deletedBlocks.map((id) => {
                  const block = MINECRAFT_BLOCKS.find(b => b.value === id);
                  return (
                    <span key={id} className="cp-tag cp-tag-keyword" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <CpBlockIcon block={id} size={14} />
                      {block?.label || id.replace(/_/g, ' ')}
                      <button
                        className="cp-tag-x"
                        onClick={() => sendAction('restore-deleted-block', { blockId: id })}
                        title="Restore this block"
                      >&times;</button>
                    </span>
                  );
                })}
              </div>
              <button
                className="cp-btn cp-btn-danger"
                onClick={() => sendAction('clear-deleted-blocks')}
                disabled={busy}
                style={{ marginTop: 8, fontSize: 12 }}
              >
                Clear All Deleted Blocks
              </button>
            </div>}
          </div>

          {/* Right column */}
          <div className="cp-col">
            {/* Actions */}
            <div className="cp-card">
              <div className="cp-card-label">Actions</div>
              <div className="cp-actions">
                {isIdle ? (
                  <button className="cp-btn cp-btn-start" onClick={handleStart} disabled={busy}>
                    Start Event
                  </button>
                ) : (
                  <>
                    {isPaused ? (
                      <button className="cp-btn cp-btn-start" onClick={() => sendAction('resume')} disabled={busy}>
                        Resume
                      </button>
                    ) : (
                      <button className="cp-btn cp-btn-warn" onClick={() => sendAction('pause')} disabled={busy}>
                        Pause
                      </button>
                    )}
                    {rs === 'intermission' ? (
                      <button className="cp-btn" onClick={() => sendAction('end-round')} disabled={busy}>
                        Skip Intermission
                      </button>
                    ) : (
                      <button className="cp-btn" onClick={() => sendAction('end-round')} disabled={busy || (rs !== 'active' && rs !== 'paused')}>
                        End Round
                      </button>
                    )}
                    <button className="cp-btn cp-btn-danger" onClick={() => sendAction('reset')} disabled={busy}>
                      Reset
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Config */}
            <div className="cp-card">
              <div className="cp-card-label">Configuration</div>
              <div className="cp-config-grid">
                <div className="cp-config-field">
                  <label className="cp-config-label">Round Duration</label>
                  <div className="cp-config-input-row">
                    <input
                      type="number"
                      className="cp-config-input"
                      value={editDuration}
                      onChange={(e) => setEditDuration(parseInt(e.target.value) || 60)}
                      min={30} max={3600}
                    />
                    <span className="cp-config-unit">sec</span>
                    <button
                      className="cp-btn cp-btn-xs"
                      onClick={() => sendAction('set-duration', { roundDurationSec: editDuration })}
                      disabled={busy}
                    >Apply</button>
                  </div>
                  <span className="cp-config-hint">{Math.floor(editDuration / 60)}m {editDuration % 60}s</span>
                </div>
                <div className="cp-config-field">
                  <label className="cp-config-label">Intermission</label>
                  <div className="cp-config-input-row">
                    <input
                      type="number"
                      className="cp-config-input"
                      value={editIntermission}
                      onChange={(e) => setEditIntermission(parseInt(e.target.value) || 10)}
                      min={5} max={300}
                    />
                    <span className="cp-config-unit">sec</span>
                    <button
                      className="cp-btn cp-btn-xs"
                      onClick={() => sendAction('set-intermission', { intermissionSec: editIntermission })}
                      disabled={busy}
                    >Apply</button>
                  </div>
                  <span className="cp-config-hint">{editIntermission}s between rounds</span>
                </div>
                {isWyr && (
                  <div className="cp-config-field">
                    <label className="cp-config-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={editIntensified}
                        onChange={(e) => {
                          setEditIntensified(e.target.checked);
                          sendAction('set-intensified', { intensified: e.target.checked });
                        }}
                        disabled={busy}
                      />
                      Intensified Mode
                    </label>
                    <span className="cp-config-hint">Green/purple colors with glow effects</span>
                  </div>
                )}
                {isChunkDelete && (
                  <div className="cp-config-field">
                    <label className="cp-config-label">Border Radius</label>
                    <div className="cp-config-input-row">
                      <input
                        type="number"
                        className="cp-config-input"
                        value={editBorderRadius}
                        onChange={(e) => setEditBorderRadius(parseInt(e.target.value) || 500)}
                        min={50} max={5000}
                      />
                      <span className="cp-config-unit">blocks</span>
                      <button
                        className="cp-btn cp-btn-xs"
                        onClick={() => sendAction('set-border', { borderRadius: editBorderRadius })}
                        disabled={busy}
                      >Apply</button>
                    </div>
                    <span className="cp-config-hint">&plusmn;{editBorderRadius} = {editBorderRadius * 2}x{editBorderRadius * 2} area</span>
                  </div>
                )}
              </div>
              {isChunkDelete && (
                <div style={{ marginTop: '0.75rem' }}>
                  <button
                    className="cp-btn cp-btn-warn"
                    onClick={() => sendAction('undo-delete')}
                    disabled={busy || !roomState?.history?.length}
                    style={{ width: '100%' }}
                  >
                    Undo Last Deletion ({roomState?.deletedChunks?.length || 0} deleted)
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Bottom Row: Commands/Chunks/Blocks + History ── */}
        <div className="cp-grid-bottom">
          {/* Live Commands (command-voting) */}
          {!isChunkDelete && !isBlockDelete && (
          <div className="cp-card">
            <div className="cp-card-label">
              Commands {roomState?.commands?.length ? `(${roomState.commands.length})` : ''}
            </div>
            {roomState?.commands?.length > 0 ? (
              <div className="cp-commands">
                {roomState.commands.map((cmd) => (
                  <div key={cmd.id} className="cp-command">
                    <span className="cp-command-votes">{cmd.votes}</span>
                    <span className="cp-command-text">{cmd.command}</span>
                    <span className="cp-command-by">{cmd.displayName}</span>
                    <button
                      className="cp-command-delete"
                      onClick={() => sendAction('delete-command', { commandId: cmd.id })}
                      disabled={busy}
                      title="Delete command"
                    >&times;</button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="cp-empty">No commands this round</div>
            )}
          </div>
          )}

          {/* Chunk Votes (chunk-delete) */}
          {isChunkDelete && (
          <div className="cp-card">
            <div className="cp-card-label">
              Chunk Votes {roomState?.chunkVotes ? `(${Object.keys(roomState.chunkVotes).length} chunks)` : ''}
            </div>
            {roomState?.chunkVotes && Object.keys(roomState.chunkVotes).length > 0 ? (
              <div className="cp-commands">
                {Object.entries(roomState.chunkVotes)
                  .sort(([, a], [, b]) => b - a)
                  .map(([key, votes]) => (
                    <div key={key} className="cp-command">
                      <span className="cp-command-votes">{votes}</span>
                      <span className="cp-command-text">Chunk [{key}]</span>
                    </div>
                  ))
                }
              </div>
            ) : (
              <div className="cp-empty">No votes this round</div>
            )}
          </div>
          )}

          {/* Block Votes (block-delete) */}
          {isBlockDelete && (
          <div className="cp-card">
            <div className="cp-card-label">
              Block Votes {roomState?.blockVotes ? `(${Object.keys(roomState.blockVotes).length} blocks)` : ''}
            </div>
            {roomState?.blockVotes && Object.keys(roomState.blockVotes).length > 0 ? (
              <div className="cp-commands">
                {Object.entries(roomState.blockVotes)
                  .sort(([, a], [, b]) => b - a)
                  .map(([blockId, votes]) => {
                    const block = MINECRAFT_BLOCKS.find(b => b.value === blockId);
                    return (
                      <div key={blockId} className="cp-command">
                        <span className="cp-command-votes">{votes}</span>
                        <span className="cp-command-text" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <CpBlockIcon block={blockId} size={18} />
                          {block?.label || blockId.replace(/_/g, ' ')}
                        </span>
                      </div>
                    );
                  })
                }
              </div>
            ) : (
              <div className="cp-empty">No votes this round</div>
            )}
          </div>
          )}

          {/* History */}
          <div className="cp-card">
            <div className="cp-card-label">Round History</div>
            {roomState?.history?.length > 0 ? (
              <div className="cp-history">
                {[...roomState.history].reverse().map((h) => {
                  let winnerLabel;
                  if (isChunkDelete) {
                    winnerLabel = `Chunk [${h.winner.chunkX}, ${h.winner.chunkZ}]`;
                  } else if (isBlockDelete) {
                    const block = MINECRAFT_BLOCKS.find(b => b.value === h.winner.blockId);
                    winnerLabel = block?.label || h.winner.blockId?.replace(/_/g, ' ') || '?';
                  } else {
                    winnerLabel = h.winner.command;
                  }
                  return (
                    <div key={h.roundNumber} className="cp-history-item">
                      <span className="cp-history-round">R{h.roundNumber}</span>
                      <span className="cp-history-cmd" style={isBlockDelete ? { display: 'flex', alignItems: 'center', gap: 4 } : undefined}>
                        {isBlockDelete && <CpBlockIcon block={h.winner.blockId} size={16} />}
                        {winnerLabel}
                      </span>
                      <span className="cp-history-meta">
                        {!isChunkDelete && !isBlockDelete && <>{h.winner.displayName} &middot; </>}{h.winner.votes} vote{h.winner.votes !== 1 ? 's' : ''}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="cp-empty">No rounds completed yet</div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
